import { useEffect, useState, useCallback } from 'react';
import prodApi from '../../services/prodApi';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Plus, Pencil, Trash2, Package, AlertTriangle, Layers, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { ExportButton } from '../../components/produccion/ExportButton';

const TIPOS_ARTICULO = [
  { value: 'PRODUCTO_ALMACENABLE', label: 'Producto Almacenable' },
  { value: 'SERVICIO', label: 'Servicio' },
  { value: 'PRODUCTO_NO_ALMACENABLE', label: 'Producto No Almacenable' }
];

const UNIDADES = ['UND', 'KG', 'M', 'LT', 'PAR', 'ROLLO', 'CAJA', 'BOLSA', 'PAQUETE'];

export const Inventario = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState('todos'); // 'todos', 'activos', 'inactivos'
  
  // Catálogos
  const [proveedores, setProveedores] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  
  // Secciones colapsables
  const [seccionBasica, setSeccionBasica] = useState(true);
  const [seccionInventario, setSeccionInventario] = useState(true);
  const [seccionCompras, setSeccionCompras] = useState(true);

  const initialFormData = {
    codigo: '',
    nombre: '',
    descripcion: '',
    tipo_articulo: 'PRODUCTO_ALMACENABLE',
    unidad_medida: 'UND',
    categoria_id: '',
    marca_id: '',
    stock_minimo: '',
    punto_reorden: '',
    descripcion_compras: '',
    costo_compra: '',
    proveedor_id: '',
    control_por_rollos: false,
    activo: true
  };

  const [formData, setFormData] = useState(initialFormData);

  // Filtrar items
  const filteredItems = items.filter(item => {
    // Filtro de búsqueda
    const matchSearch = searchTerm === '' || 
      (item.codigo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro de activo/inactivo
    const matchActivo = filterActivo === 'todos' || 
      (filterActivo === 'activos' && item.activo !== false) ||
      (filterActivo === 'inactivos' && item.activo === false);
    
    return matchSearch && matchActivo;
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes] = await Promise.all([
        prodApi.get('/inventario')
      ]);
      setItems(itemsRes.data || []);
      
      // Cargar proveedores desde finanzas (endpoint global)
      try {
        const provRes = await api.get('/proveedores');
        setProveedores(provRes.data || []);
      } catch (e) {
        setProveedores([]);
      }
      
      // Cargar marcas y categorías
      try {
        const [marcasRes, catsRes] = await Promise.all([
          prodApi.get('/marcas'),
          prodApi.get('/inventario-categorias')
        ]);
        setMarcas(marcasRes.data || []);
        setCategorias(catsRes.data || []);
      } catch (e) {
        setMarcas([]);
        setCategorias([]);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigo: item.codigo || '',
        nombre: item.nombre || '',
        descripcion: item.descripcion || '',
        tipo_articulo: item.tipo_articulo || 'PRODUCTO_ALMACENABLE',
        unidad_medida: item.unidad_medida || 'UND',
        categoria_id: item.categoria_id?.toString() || '',
        marca_id: item.marca_id || '',
        stock_minimo: item.stock_minimo?.toString() || '',
        punto_reorden: item.punto_reorden?.toString() || item.stock_minimo?.toString() || '',
        descripcion_compras: item.descripcion_compras || '',
        costo_compra: item.costo_compra?.toString() || '',
        proveedor_id: item.proveedor_id?.toString() || '',
        control_por_rollos: item.control_por_rollos || false,
        activo: item.activo !== false
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.codigo) {
      toast.error('El código es requerido');
      return;
    }

    const payload = {
      codigo: formData.codigo,
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      tipo_articulo: formData.tipo_articulo,
      unidad_medida: formData.unidad_medida,
      categoria_id: formData.categoria_id ? parseInt(formData.categoria_id) : null,
      marca_id: formData.marca_id || null,
      stock_minimo: formData.stock_minimo ? parseInt(formData.stock_minimo) : 0,
      punto_reorden: formData.punto_reorden ? parseFloat(formData.punto_reorden) : 0,
      descripcion_compras: formData.descripcion_compras || null,
      costo_compra: formData.costo_compra ? parseFloat(formData.costo_compra) : 0,
      proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id) : null,
      control_por_rollos: formData.control_por_rollos,
      activo: formData.activo
    };

    try {
      if (editingItem) {
        await prodApi.put(`/inventario/${editingItem.id}`, payload);
        toast.success('Artículo actualizado');
      } else {
        await prodApi.post('/inventario', payload);
        toast.success('Artículo creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await prodApi.delete(`/inventario/${deleteConfirm.id}`);
      toast.success('Artículo eliminado');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleActivo = async (item) => {
    try {
      await prodApi.put(`/inventario/${item.id}`, { activo: !item.activo });
      toast.success(`Artículo ${!item.activo ? 'activado' : 'desactivado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const getTipoLabel = (tipo) => {
    const found = TIPOS_ARTICULO.find(t => t.value === tipo);
    return found ? found.label : 'Producto';
  };

  const getTipoBadgeColor = (tipo) => {
    switch (tipo) {
      case 'SERVICIO': return 'bg-blue-100 text-blue-800';
      case 'PRODUCTO_ALMACENABLE': return 'bg-green-100 text-green-800';
      case 'PRODUCTO_NO_ALMACENABLE': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatus = (item) => {
    if (item.stock_actual <= 0) return 'danger';
    if (item.stock_actual <= (item.punto_reorden || item.stock_minimo || 0)) return 'warning';
    return 'success';
  };

  return (
    <div className="space-y-6" data-testid="inventario-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventario</h1>
            <p className="text-sm text-gray-500">Gestión de artículos y productos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <ExportButton tabla="inventario" />
          <Button onClick={() => handleOpenDialog()} data-testid="btn-nuevo-item">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Artículo
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por código, nombre o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <Select value={filterActivo} onValueChange={setFilterActivo}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="activos">Solo activos</SelectItem>
              <SelectItem value="inactivos">Solo inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-gray-500 flex items-center">
          {filteredItems.length} de {items.length} artículos
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {items.length === 0 ? 'No hay artículos registrados' : 'No se encontraron artículos con los filtros aplicados'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Unidad</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Activo</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id} data-testid={`item-row-${item.id}`} className={item.activo === false ? 'opacity-50' : ''}>
                    <TableCell className="font-mono text-sm">{item.codigo || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.nombre}</span>
                        {item.control_por_rollos && (
                          <Badge variant="outline" className="text-xs">
                            <Layers className="h-3 w-3 mr-1" />Rollos
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTipoBadgeColor(item.tipo_articulo)}>
                        {getTipoLabel(item.tipo_articulo)}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.unidad_medida || 'UND'}</TableCell>
                    <TableCell className="text-sm text-gray-600">{item.proveedor_nombre || '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {item.tipo_articulo === 'PRODUCTO_ALMACENABLE' || !item.tipo_articulo ? (
                        <span className={getStockStatus(item) === 'danger' ? 'text-red-600 font-semibold' : 
                                        getStockStatus(item) === 'warning' ? 'text-amber-600' : ''}>
                          {parseFloat(item.stock_actual || 0).toFixed(2)}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-gray-600">
                      S/ {parseFloat(item.costo_promedio || item.costo_compra || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-emerald-600">
                      S/ {(parseFloat(item.stock_actual || 0) * parseFloat(item.costo_promedio || item.costo_compra || 0)).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {getStockStatus(item) === 'warning' && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />Bajo
                        </Badge>
                      )}
                      {getStockStatus(item) === 'danger' && (
                        <Badge className="bg-red-100 text-red-700">Sin stock</Badge>
                      )}
                      {getStockStatus(item) === 'success' && (
                        <Badge className="bg-green-100 text-green-700">OK</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={item.activo !== false}
                        onCheckedChange={() => handleToggleActivo(item)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(item)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} Artículo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Información Básica */}
            <Collapsible open={seccionBasica} onOpenChange={setSeccionBasica}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Información Básica</CardTitle>
                      {seccionBasica ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Código *</Label>
                        <Input 
                          value={formData.codigo} 
                          onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                          placeholder="SKU o código interno"
                          required
                          className="font-mono"
                        />
                      </div>
                      <div>
                        <Label>Tipo de Artículo</Label>
                        <Select value={formData.tipo_articulo} onValueChange={(v) => setFormData({ ...formData, tipo_articulo: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIPOS_ARTICULO.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Nombre *</Label>
                      <Input 
                        value={formData.nombre} 
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} 
                        placeholder="Nombre del artículo"
                        required 
                      />
                    </div>

                    <div>
                      <Label>Descripción</Label>
                      <Textarea 
                        value={formData.descripcion} 
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        placeholder="Descripción del artículo"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Unidad de Medida</Label>
                        <Select value={formData.unidad_medida} onValueChange={(v) => setFormData({ ...formData, unidad_medida: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNIDADES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Categoría</Label>
                        <Select value={formData.categoria_id || "none"} onValueChange={(v) => setFormData({ ...formData, categoria_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin categoría</SelectItem>
                            {categorias.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Marca</Label>
                        <Select value={formData.marca_id || "none"} onValueChange={(v) => setFormData({ ...formData, marca_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin marca</SelectItem>
                            {marcas.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <Checkbox 
                          id="control_por_rollos"
                          checked={formData.control_por_rollos}
                          onCheckedChange={(checked) => setFormData({ ...formData, control_por_rollos: checked })}
                        />
                        <Label htmlFor="control_por_rollos" className="cursor-pointer">
                          Control por rollos (para telas)
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Inventario */}
            {(formData.tipo_articulo === 'PRODUCTO_ALMACENABLE' || !formData.tipo_articulo) && (
              <Collapsible open={seccionInventario} onOpenChange={setSeccionInventario}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Información del Inventario</CardTitle>
                        {seccionInventario ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                      <div>
                        <Label>Stock Mínimo (Punto de Reorden)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.punto_reorden || formData.stock_minimo} 
                          onChange={(e) => setFormData({ ...formData, punto_reorden: e.target.value, stock_minimo: e.target.value })}
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">Alerta cuando el stock llegue a este nivel</p>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Compras */}
            <Collapsible open={seccionCompras} onOpenChange={setSeccionCompras}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Compras</CardTitle>
                      {seccionCompras ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div>
                      <Label>Descripción para Compras</Label>
                      <Textarea 
                        value={formData.descripcion_compras} 
                        onChange={(e) => setFormData({ ...formData, descripcion_compras: e.target.value })}
                        placeholder="Descripción que aparecerá en las órdenes de compra"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Costo de Compra</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.costo_compra} 
                          onChange={(e) => setFormData({ ...formData, costo_compra: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Proveedor Preferido</Label>
                        <Select value={formData.proveedor_id || "none"} onValueChange={(v) => setFormData({ ...formData, proveedor_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin proveedor preferido</SelectItem>
                            {proveedores.map(p => (
                              <SelectItem key={p.id} value={p.id.toString()}>{p.razon_social || p.nro_doc}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">{editingItem ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar artículo?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            ¿Estás seguro de eliminar <strong>{deleteConfirm?.nombre}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventario;
